import client from '../client';
import type { ApiResponse } from '../../types/api';
import { PAY_URL } from '../../lib/constants';

export interface Expense {
  _id: string;
  storeId: string;
  amount: number;
  expense: string;
  billingCycle: string;
  paymentMethod: string;
  status: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface MembershipStatus {
  plan: string;
  activePlan: string;
  isExpired: boolean;
  expiresSoon: boolean;
  startDate: string | null;
  endDate: string | null;
  daysRemaining: number;
  needsRenewal: boolean;
}

export const expensesApi = {
  getHistory: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ expenses: Expense[] }>>(
      `/dashboard/${storeId}/expenses/history`,
    );
    return res.data.data!;
  },

  getBalance: async (storeId: string) => {
    const res = await client.get<ApiResponse<{ balance: number; currency: string }>>(
      `/dashboard/${storeId}/expenses/balance`,
    );
    return res.data.data!;
  },

  getMembershipStatus: async (storeId: string) => {
    const res = await client.get<ApiResponse<MembershipStatus>>(
      `/dashboard/${storeId}/expenses/membership/status`,
    );
    return res.data.data!;
  },

  /**
   * Purchase a membership plan. `paymentMethod` is required so the caller
   * (the payment chooser UI) picks the path explicitly — passing 'balance'
   * will 422 if the wallet can't cover it instead of silently falling back
   * to card.
   */
  purchaseMembership: async (
    storeId: string,
    plan: string,
    paymentMethod: 'balance' | 'card',
    cycle: 'monthly' | 'yearly' = 'monthly',
  ) => {
    const res = await client.post<ApiResponse<PurchaseMembershipResponse>>(
      `/dashboard/${storeId}/expenses/membership/purchase`,
      { plan, paymentMethod, cycle },
    );
    return res.data.data!;
  },

  /**
   * Poll the pay-session status. Lives at the pay server root, not under
   * /api/v1, so we bypass the axios client.
   */
  getSessionStatus: async (sessionId: string): Promise<{ payed: number; transactionState?: string }> => {
    const res = await fetch(`${PAY_URL}/session/${sessionId}/status`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('session_status_failed');
    return res.json();
  },
};

export type PurchaseMembershipResponse =
  | { paymentMethod: 'balance'; expenseId: string }
  | { paymentMethod: 'card'; sessionId: string; paymentUrl: string };
